import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

export default function KakobuySpreadsheetPage() {
  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="w-full max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">Strona główna</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium whitespace-nowrap">Kakobuy Spreadsheet</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wider uppercase">Poradnik</span>
              <span className="text-xs text-foreground-400">15 lipca 2026</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">12 min czytania</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 font-heading leading-tight">
              Kakobuy Spreadsheet Polska – kompletny przewodnik po najlepszych Kakobuy Finds
            </h1>
            <p className="text-base md:text-lg text-foreground-600 mt-5 leading-relaxed max-w-3xl">
              Kakobuy Spreadsheet to jedno z najpopularniejszych rozwiązań dla osób, które chcą szybciej znajdować produkty z Chin.
              Zamiast godzinami przeszukiwać tysiące ofert, użytkownicy mogą korzystać z uporządkowanych list produktów
              zawierających najważniejsze informacje, takie jak nazwa produktu, kategoria, cena, link oraz dodatkowe wskazówki.
            </p>
          </header>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Organized%20workspace%20with%20laptop%20displaying%20detailed%20spreadsheet%20data%20colorful%20sticky%20notes%20on%20monitor%20frame%20multiple%20fashion%20product%20photos%20pinned%20to%20cork%20board%20behind%20creative%20planning%20session%20atmosphere%20warm%20desk%20lamp%20lighting%20productivity%20aesthetic%20editorial%20photography&width=1200&height=600&seq=pillar-hero-unique-v2&orientation=landscape"
              alt="Kakobuy Spreadsheet - zorganizowana lista produktów"
              className="w-full h-auto"
            />
          </div>

          {/* Section 1: Czym jest Kakobuy Spreadsheet */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              1. Czym jest Kakobuy Spreadsheet?
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              Kakobuy Spreadsheet to uporządkowana baza produktów przygotowana w formie tabeli, która zawiera starannie wyselekcjonowane
              przedmioty dostępne na chińskich platformach zakupowych. Arkusz ten został stworzony przez doświadczonych użytkowników
              społeczności Kakobuy i jest regularnie aktualizowany, aby odzwierciedlać najnowsze trendy i najpopularniejsze znaleziska.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              W przeciwieństwie do standardowego przeglądania ofert, gdzie użytkownik musi ręcznie filtrować setki niepowiązanych produktów,
              Kakobuy Spreadsheet dostarcza gotową, skategoryzowaną listę zawierającą wszystkie kluczowe informacje potrzebne do podjęcia
              decyzji zakupowej. To narzędzie, które radykalnie skraca czas potrzebny na znalezienie interesujących produktów i eliminuje
              konieczność żmudnego przeszukiwania forów oraz grup społecznościowych.
            </p>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60 mb-6">
              <h3 className="text-lg font-bold text-foreground-900 font-heading mb-4">Co zawiera Kakobuy Spreadsheet?</h3>
              <ul className="space-y-2.5">
                {[
                  'Nazwy produktów wraz z dokładnymi opisami',
                  'Kategorie ułatwiające nawigację po liście',
                  'Aktualne ceny w różnych wariantach',
                  'Bezpośrednie linki do stron produktów',
                  'Informacje o sprzedawcach i ich reputacji',
                  'Popularne wybory społeczności Kakobuy',
                  'Dodatkowe wskazówki i rekomendacje zakupowe',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground-700">
                    <span className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-600 text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Metoda wyszukiwania</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Czas</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Trudność</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Efekt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">Samodzielne szukanie</td>
                    <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">Wysoki</td>
                    <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">Trudne</td>
                    <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">Losowe wyniki</td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors bg-accent-50/50">
                    <td className="px-5 py-3 text-accent-800 font-semibold whitespace-nowrap">Kakobuy Spreadsheet</td>
                    <td className="px-5 py-3 text-accent-800 font-semibold whitespace-nowrap">Niski</td>
                    <td className="px-5 py-3 text-accent-800 font-semibold whitespace-nowrap">Łatwe</td>
                    <td className="px-5 py-3 text-accent-800 font-semibold whitespace-nowrap">Gotowe produkty</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2: Dlaczego warto */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              2. Dlaczego warto korzystać z Kakobuy Spreadsheet?
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Korzystanie z Kakobuy Spreadsheet przynosi wymierne korzyści każdemu, kto regularnie dokonuje zakupów na chińskich
              platformach. Niezależnie od tego, czy jesteś początkującym entuzjastą mody ulicznej, czy doświadczonym kupcem
              poszukującym okazji – odpowiednio przygotowany arkusz produktów znacząco usprawni Twój proces zakupowy.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Oszczędność czasu', desc: 'Zamiast spędzać godziny na ręcznym przeszukiwaniu ofert, otrzymujesz gotową, sprawdzoną listę produktów w kilka sekund.' },
                { title: 'Dostęp do trendów', desc: 'Spreadsheet zawiera produkty, które są obecnie najpopularniejsze – nie musisz śledzić dziesiątek grup i forów.' },
                { title: 'Lepsza organizacja', desc: 'Wszystkie informacje w jednym miejscu: nazwa, kategoria, cena, rozmiar i bezpośredni link do produktu.' },
                { title: 'Łatwiejsze porównywanie', desc: 'Dzięki ujednoliconej strukturze możesz szybko porównać kilka podobnych produktów i wybrać najlepszy.' },
                { title: 'Dla początkujących', desc: 'Idealne rozwiązanie jeśli dopiero zaczynasz – nie musisz znać chińskich platform ani skomplikowanych wyszukiwarek.' },
                { title: 'Regularne aktualizacje', desc: 'Lista jest stale odświeżana, dzięki czemu zawsze masz dostęp do aktualnych ofert i nowych znalezisk.' },
              ].map((item, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/60 transition-all duration-200 hover:border-accent-200">
                  <h4 className="text-base font-bold text-foreground-900 font-heading mb-2">{item.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Funkcja</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Zwykłe wyszukiwanie</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Spreadsheet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  {[
                    ['Lista produktów', 'Nie', 'Tak'],
                    ['Kategorie', 'Ograniczone', 'Tak'],
                    ['Porównanie', 'Trudne', 'Łatwe'],
                    ['Aktualizacja', 'Brak', 'Regularna'],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3 text-foreground-700 font-medium whitespace-nowrap">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row[1]}</td>
                      <td className="px-5 py-3 text-accent-700 font-semibold whitespace-nowrap">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3: Jak działa */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              3. Jak działa Kakobuy Spreadsheet?
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-8">
              Proces korzystania z Kakobuy Spreadsheet jest niezwykle prosty i intuicyjny. Poniżej przedstawiamy szczegółową
              instrukcję krok po kroku, która pomoże Ci sprawnie rozpocząć poszukiwanie idealnych produktów.
            </p>

            <div className="relative pl-8 border-l-2 border-accent-200 space-y-10">
              {[
                { step: 'Krok 1', title: 'Wybierz kategorię produktu', desc: 'Na stronie głównej znajdziesz rozwijane menu z czternastoma kategoriami, w tym Buty, Bluzy, Koszulki, Kurtki, Torby i wiele innych. Wybierz tę, która Cię interesuje, aby przejść do odpowiedniej sekcji w głównym serwisie.' },
                { step: 'Krok 2', title: 'Sprawdź dostępne produkty', desc: 'Po wybraniu kategorii zobaczysz listę wszystkich dostępnych produktów w arkuszu. Każdy wpis zawiera szczegółowe informacje, które pomogą Ci ocenić dany przedmiot przed podjęciem decyzji.' },
                { step: 'Krok 3', title: 'Przeanalizuj informacje', desc: 'Zwróć uwagę na cenę, dostępne rozmiary, opinie innych użytkowników oraz dodatkowe uwagi zawarte w arkuszu. Porównaj kilka podobnych produktów, aby wybrać najlepszą opcję.' },
                { step: 'Krok 4', title: 'Sprawdź bezpieczeństwo sprzedawcy', desc: 'Przed zakupem warto zweryfikować reputację sprzedawcy – sprawdź jego oceny, datę rejestracji oraz opinie od innych kupujących. Arkusz często zawiera takie informacje.' },
                { step: 'Krok 5', title: 'Przejdź do głównego serwisu Kakobuy', desc: 'Gdy znajdziesz interesujący produkt, kliknij w odpowiedni link, aby przejść bezpośrednio do strony produktu w głównym serwisie i sfinalizować zakup.' },
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[2.15rem] top-1 w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                    <span className="text-background-50 text-xs font-bold">{i + 1}</span>
                  </div>
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">{item.step}</span>
                  <h3 className="text-lg font-bold text-foreground-900 font-heading mt-1 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4: Kategorie */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              4. Najpopularniejsze kategorie Kakobuy Spreadsheet
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Kakobuy Spreadsheet obejmuje szeroki zakres kategorii produktowych, dzięki czemu każdy znajdzie coś dla siebie.
              Poniżej przedstawiamy najczęściej przeglądane kategorie wraz z przykładami produktów.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="p-4 rounded-xl bg-background-100 border border-background-200/60 text-center transition-all duration-200 hover:border-accent-300 hover:bg-accent-50/50 cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-accent-200 transition-colors">
                    <i className="ri-shopping-bag-line text-accent-600"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">{cat.name}</p>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">{cat.nameEn}</p>
                </a>
              ))}
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Kategoria</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Popularność</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Przykłady</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  {[
                    ['Buty', 'Bardzo wysoka', 'Sneakersy, sportowe, casual'],
                    ['Bluzy/Swetry', 'Wysoka', 'Hoodies, crewneck, zip-up'],
                    ['Koszulki', 'Wysoka', 'T-shirty basic, graphic tee'],
                    ['Kurtki', 'Wysoka', 'Puchowe, bomberki, wiatrówki'],
                    ['Torby', 'Średnia', 'Plecaki, torby na ramię'],
                    ['Akcesoria', 'Średnia', 'Czapki, biżuteria, okulary'],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3 text-foreground-700 font-medium whitespace-nowrap">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row[1]}</td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5: Dla początkujących */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              5. Kakobuy Spreadsheet dla początkujących
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Jeśli nigdy wcześniej nie korzystałeś z Kakobuy ani podobnych platform, nie martw się – Kakobuy Spreadsheet został
              zaprojektowany z myślą o użytkownikach na każdym poziomie zaawansowania. Poniżej znajdziesz praktyczny przewodnik
              pierwszego zakupu, który przeprowadzi Cię przez cały proces od wyboru produktu po finalizację zamówienia.
            </p>

            <div className="bg-secondary-50 rounded-2xl p-6 md:p-8 border border-secondary-200/60 mb-6">
              <h3 className="text-lg font-bold text-foreground-900 font-heading mb-4">Pierwszy zakup krok po kroku</h3>
              <div className="space-y-4">
                {[
                  { title: 'Zacznij od przeglądania kategorii', desc: 'Nie musisz od razu nic kupować. Po prostu przejrzyj dostępne kategorie na stronie głównej i zobacz, jakie produkty Cię interesują.' },
                  { title: 'Wybierz kilka produktów do porównania', desc: 'Znajdź 2-3 podobne produkty i porównaj ich ceny, opinie oraz informacje o sprzedawcach. Arkusz ułatwi Ci to zadanie.' },
                  { title: 'Sprawdź opinie społeczności', desc: 'Wiele produktów w arkuszu ma dodatkowe uwagi od innych użytkowników – to bezcenne źródło informacji dla początkujących.' },
                  { title: 'Zacznij od niedrogiego produktu', desc: 'Na pierwszy zakup wybierz coś w niższej cenie – dzięki temu przetestujesz cały proces bez dużego ryzyka finansowego.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-accent-700 text-sm font-bold">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-foreground-900">{item.title}</h4>
                      <p className="text-sm text-foreground-600 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Section 6: Bezpieczeństwo */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              6. Czy Kakobuy Spreadsheet jest bezpieczny?
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              Ta strona jest niezależnym przewodnikiem i <strong>nie jest bezpośrednio powiązana z platformą Kakobuy</strong>.
              Naszym celem jest dostarczenie przydatnych informacji i ułatwienie dostępu do uporządkowanych list produktów.
              Pamiętaj jednak, że ostateczna odpowiedzialność za bezpieczeństwo zakupów spoczywa na Tobie.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Aby zmaksymalizować bezpieczeństwo swoich transakcji, zawsze stosuj się do poniższych zasad:
            </p>

            <div className="overflow-x-auto rounded-xl border border-background-200/60 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Element</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900">Dlaczego ważne</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  {[
                    ['QC zdjęcia', 'Pomagają ocenić rzeczywisty stan produktu przed wysyłką – zawsze proś o zdjęcia kontrolne.'],
                    ['Opinie innych', 'Pokazują doświadczenia poprzednich kupujących z tym samym sprzedawcą i produktem.'],
                    ['Cena produktu', 'Pozwala uniknąć podejrzanie tanich ofert, które mogą być próbą oszustwa.'],
                    ['Reputacja sprzedawcy', 'Sprawdź datę rejestracji, liczbę transakcji i ogólną ocenę sprzedawcy.'],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3 text-foreground-700 font-semibold whitespace-nowrap">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground-600 leading-relaxed">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 7: Porównanie */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
              7. Kakobuy Spreadsheet vs inne metody
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Na rynku istnieje wiele sposobów na znajdowanie produktów z Chin, jednak nie wszystkie są równie efektywne.
              Poniższa tabela przedstawia porównanie Kakobuy Spreadsheet z najpopularniejszymi alternatywami.
            </p>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900 whitespace-nowrap">Rozwiązanie</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900">Zalety</th>
                    <th className="text-left px-5 py-3 font-semibold text-foreground-900">Wady</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-5 py-3 text-foreground-700 font-medium whitespace-nowrap">Google Search</td>
                    <td className="px-5 py-3 text-foreground-600">Dużo wyników, łatwy dostęp</td>
                    <td className="px-5 py-3 text-foreground-600">Brak organizacji, przypadkowe wyniki</td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-5 py-3 text-foreground-700 font-medium whitespace-nowrap">Social Media</td>
                    <td className="px-5 py-3 text-foreground-600">Inspiracje, zdjęcia od użytkowników</td>
                    <td className="px-5 py-3 text-foreground-600">Trudno znaleźć konkretne linki</td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors bg-accent-50/50">
                    <td className="px-5 py-3 text-accent-800 font-semibold whitespace-nowrap">Kakobuy Spreadsheet</td>
                    <td className="px-5 py-3 text-accent-800 font-semibold">Gotowe listy, kategorie, ceny</td>
                    <td className="px-5 py-3 text-accent-800 font-semibold">Wymaga aktualizacji</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 8: FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
              8. Najczęściej zadawane pytania (FAQ)
            </h2>
            <div className="space-y-3">
              {[
                { q: 'Co to jest Kakobuy Spreadsheet?', a: 'Kakobuy Spreadsheet to uporządkowana lista produktów w formie arkusza, która zawiera starannie wyselekcjonowane przedmioty z chińskich platform zakupowych. Zawiera informacje takie jak nazwa produktu, kategoria, cena, link oraz opinie społeczności – wszystko w jednym miejscu dla wygody użytkownika.' },
                { q: 'Czy Kakobuy Spreadsheet jest darmowy?', a: 'Tak, dostęp do list kategorii i poradników na tej stronie jest całkowicie bezpłatny. Arkusz został stworzony przez społeczność i jest udostępniany bez żadnych opłat. Zachęcamy do odwiedzenia głównego serwisu po więcej informacji.' },
                { q: 'Jak znaleźć produkty Kakobuy?', a: 'Najprostszym sposobem jest skorzystanie z menu kategorii na stronie głównej. Wybierz interesującą Cię kategorię (np. Buty, Bluzy, Kurtki), a zostaniesz przekierowany do odpowiedniej sekcji w głównym serwisie, gdzie znajdziesz pełną listę produktów.' },
                { q: 'Czy Kakobuy działa w Polsce?', a: 'Tak, Kakobuy jest dostępny dla użytkowników z Polski. Platforma umożliwia zakup produktów z Chin i ich wysyłkę do Polski. Warto jednak zawsze sprawdzić koszty dostawy oraz ewentualne opłaty celne przed finalizacją zamówienia.' },
                { q: 'Czy warto korzystać z Kakobuy Finds?', a: 'Zdecydowanie tak! Kakobuy Finds to starannie wyselekcjonowana kolekcja produktów wybranych przez doświadczonych użytkowników. Dzięki temu masz dostęp do sprawdzonych, popularnych przedmiotów bez konieczności samodzielnego przeszukiwania setek ofert.' },
                { q: 'Jak sprawdzić produkt przed zakupem?', a: 'Przed zakupem zawsze sprawdź QC zdjęcia (kontrola jakości), opinie innych kupujących, reputację sprzedawcy oraz porównaj ceny z podobnymi produktami. Wszystkie te informacje są zazwyczaj dostępne w arkuszu lub na stronie produktu.' },
                { q: 'Czy ceny produktów są stałe?', a: 'Ceny w arkuszu są orientacyjne i mogą ulec zmianie. Zawsze sprawdzaj aktualną cenę na stronie produktu przed dokonaniem zakupu, ponieważ ceny na chińskich platformach mogą się wahać w zależności od sezonu, promocji i dostępności.' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden transition-all duration-200">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/40 transition-colors">
                    <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Section 9: CTA */}
          <section className="mb-12 bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-4">
              Znajdź najlepsze Kakobuy Spreadsheet już teraz
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              Nie trać czasu na bezcelowe przeszukiwanie setek ofert. Skorzystaj z gotowych, uporządkowanych list produktów
              i odkryj najpopularniejsze Kakobuy Finds w kilka sekund.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-semibold text-base hover:bg-background-100 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Przeglądaj Kakobuy Finds
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 text-background-50 font-semibold text-base border border-background-50/30 hover:bg-primary-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Wróć na stronę główną
              </Link>
            </div>
          </section>

          {/* Internal links */}
          <div className="border-t border-background-200/60 pt-8">
            <h4 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Przydatne linki</h4>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                Strona główna KakobuyFinds
              </Link>
              {categories.slice(0, 6).map((cat) => (
                <a key={cat.id} href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </article>

        {/* Structured Data Scripts */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Kakobuy Spreadsheet Polska – kompletny przewodnik po najlepszych Kakobuy Finds",
          "description": "Poznaj Kakobuy Spreadsheet Polska. Znajdź najlepsze Kakobuy Finds, produkty, linki i poradniki zakupowe. Kompletny przewodnik dla osób kupujących z Chin.",
          "datePublished": "2026-07-15",
          "dateModified": "2026-07-15",
          "author": { "@type": "Organization", "name": "KakobuyFinds" },
          "inLanguage": "pl-PL",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://kakobuyfinds.autos/kakobuy-spreadsheet" }
        }) }}></script>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://kakobuyfinds.autos/" },
            { "@type": "ListItem", "position": 2, "name": "Kakobuy Spreadsheet", "item": "https://kakobuyfinds.autos/kakobuy-spreadsheet" }
          ]
        }) }}></script>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Co to jest Kakobuy Spreadsheet?", "acceptedAnswer": { "@type": "Answer", "text": "Kakobuy Spreadsheet to uporządkowana lista produktów w formie arkusza, która zawiera starannie wyselekcjonowane przedmioty z chińskich platform zakupowych. Zawiera informacje takie jak nazwa produktu, kategoria, cena, link oraz opinie społeczności." } },
            { "@type": "Question", "name": "Czy Kakobuy Spreadsheet jest darmowy?", "acceptedAnswer": { "@type": "Answer", "text": "Tak, dostęp do list kategorii i poradników na tej stronie jest całkowicie bezpłatny. Arkusz został stworzony przez społeczność i jest udostępniany bez żadnych opłat." } },
            { "@type": "Question", "name": "Jak znaleźć produkty Kakobuy?", "acceptedAnswer": { "@type": "Answer", "text": "Najprostszym sposobem jest skorzystanie z menu kategorii na stronie głównej. Wybierz interesującą Cię kategorię, a zostaniesz przekierowany do odpowiedniej sekcji w głównym serwisie." } },
            { "@type": "Question", "name": "Czy Kakobuy działa w Polsce?", "acceptedAnswer": { "@type": "Answer", "text": "Tak, Kakobuy jest dostępny dla użytkowników z Polski. Platforma umożliwia zakup produktów z Chin i ich wysyłkę do Polski." } },
            { "@type": "Question", "name": "Czy warto korzystać z Kakobuy Finds?", "acceptedAnswer": { "@type": "Answer", "text": "Zdecydowanie tak! Kakobuy Finds to starannie wyselekcjonowana kolekcja produktów wybranych przez doświadczonych użytkowników, co zapewnia dostęp do sprawdzonych, popularnych przedmiotów." } },
            { "@type": "Question", "name": "Jak sprawdzić produkt przed zakupem?", "acceptedAnswer": { "@type": "Answer", "text": "Przed zakupem zawsze sprawdź QC zdjęcia, opinie innych kupujących, reputację sprzedawcy oraz porównaj ceny z podobnymi produktami." } },
            { "@type": "Question", "name": "Czy ceny produktów są stałe?", "acceptedAnswer": { "@type": "Answer", "text": "Ceny w arkuszu są orientacyjne i mogą ulec zmianie. Zawsze sprawdzaj aktualną cenę na stronie produktu przed dokonaniem zakupu." } },
          ]
        }) }}></script>
      </main>
      <Footer />
    </>
  );
}